import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { apiCallUserUploadedPhotos } from "../../../../Core/Apicall";
import { useLocation } from "react-router-dom";
import EmptyData from "../../../Common/EmptyData/EmptyData";
import BoxShimmer from "../../../Common/CommonShimmer/BoxShimmer";
import UserMediaPhotoGallery from "../../../Common/ImageGallery/UserMediaPhotoGallery";
import ic_play from "../../../../assets/Images/ic_play.png";

const ClickTab: React.FC = () => {
  const [photos, setPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [totalCount, setTotalCount] = useState(0);

  const [openPreview, setOpenPreview] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<any | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const userData = location.state.user;

  const fetchData = async (userId: any) => {
    setLoading(true);
    try {
      const res = await apiCallUserUploadedPhotos({
        userId,
        offset: 1,
        limit: rowsPerPage,
      });
      setPhotos(res.data || []);
      setTotalCount(res.totalCount || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(userData.userId ? userData.userId : userData._id);
  }, [userData, rowsPerPage]);

  const handleOpenVenueGallery = (item: any) => {
    setSelectedVenue(item);
    setOpenPreview(true);
  };

  return (
    <Box sx={{ position: "relative", p: 2 }}>
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          gap: 5,
          overflowX: "auto",
          flexWrap: "wrap",
          p: 2,
          scrollSnapType: "x mandatory",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {!loading &&
          photos.map((item, index) => (
            <Grid
              key={item._id || index}
              sx={{
                flex: "0 0 auto",
                scrollSnapAlign: "start",
                textAlign: "center",
                opacity: 0,
                transform: "translateY(20px)",
                borderRadius: "2px",
                animation: `fadeSlideIn 0.5s forwards ${index * 0.1}s`,
              }}
            >
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 100px)"
                gridTemplateRows="repeat(2, 100px)"
                gap={1}
                justifyContent="center"
                alignItems="center"
              >
                {Array.from({ length: 4 }).map((_, idx) => {
                  const mediaItem = item.media?.[idx];
                  const hasMore = item.media?.length > 3 && idx === 3;
                  const placeholder =
                    "https://louca-dev.s3.ap-southeast-2.amazonaws.com/venue/media/689c2d729c634fa1bc255d14/5ea5b782-44d2-4e6c-a76a-8ed4fce61447-istockphoto-1147544807-612x612.png";

                  return hasMore ? (
                    <Box
                      key={idx}
                      sx={{
                        position: "relative",
                        width: 100,
                        height: 100,
                        borderRadius: "4px",
                        overflow: "hidden",
                        cursor: "pointer",
                        boxShadow: 1,
                      }}
                      onClick={() => handleOpenVenueGallery(item)}
                    >
                      <Box
                        component="img"
                        src={
                          mediaItem?.thumbnailMediaUrl ?? mediaItem?.mediaUrl
                        }
                        alt={item.venueName}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          filter: "blur(1px) brightness(0.7)",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: 20,
                          fontWeight: "bold",
                        }}
                      >
                        +{item.media.length - 3}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      key={idx}
                      sx={{
                        width: 100,
                        height: 100,
                        position: "relative",
                        borderRadius: "4px",
                        overflow: "hidden",
                        cursor: mediaItem ? "pointer" : "default",
                        boxShadow: 1,
                      }}
                      onClick={() =>
                        mediaItem ? handleOpenVenueGallery(item) : null
                      }
                    >
                      <Box
                        component="img"
                        src={
                          mediaItem
                            ? mediaItem.thumbnailMediaUrl ?? mediaItem.mediaUrl
                            : placeholder
                        }
                        alt={item.venueName}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: mediaItem ? 1 : 0.6,
                        }}
                      />

                      {/* Play Icon Overlay */}
                      {mediaItem?.mediaType === "Video" && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              backgroundColor: "rgba(0,0,0,0.6)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={ic_play}
                              alt="Play"
                              style={{
                                width: 10,
                                height: 10,
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Typography
                variant="subtitle2"
                sx={{ color: "black", mt: 1, textAlign: "center" }}
              >
                {item.venueName}
              </Typography>
            </Grid>
          ))}

        {loading && <BoxShimmer />}

        {!loading && <EmptyData data={photos} />}

        <style>
          {`
            @keyframes fadeSlideIn {
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>
      </Box>

      {rowsPerPage < totalCount && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Typography
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "primary.main",
            }}
            onClick={() => setRowsPerPage(rowsPerPage + 9)}
          >
            View More
          </Typography>
        </Box>
      )}

      {/* Venue Photo Gallery Dialog */}
      <UserMediaPhotoGallery
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        venueId={selectedVenue?.venueId || ""}
        venueName={selectedVenue?.venueName || ""}
        userId={userData._id}
      />
    </Box>
  );
};

export default ClickTab;
